import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Petit hook de copie presse-papiers réutilisable.
 * Centralise le pattern `navigator.clipboard.writeText` (try/catch + reset).
 * Renvoie `{ copied, copy }` ; `copied` repasse à false après ~1.8s.
 */
export function useCopyToClipboard(resetMs = 1800): {
  copied: boolean;
  copy: (text: string) => Promise<boolean>;
} {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard?.writeText(text);
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), resetMs);
        return true;
      } catch {
        setCopied(false);
        return false;
      }
    },
    [resetMs],
  );

  return { copied, copy };
}
